import { createContext, useRef, useCallback, useEffect } from 'react'

import { postTelemetryBatch } from '../service/studyService'

import { SESSION_EVENTS } from './telemetryEvents'

// Study-side twin of component/workspace/context/TelemetryContext.
// Same two-store design (buffer flushed to the server every 5s + permanent
// in-memory store), same SESSION_START-on-mount behaviour, same payload shape.
//
// Differences from the workspace provider, all forced by the study's transport:
//  - keyed by participantId + stepId instead of sessionId + currentUser.id
//  - flushes on unmount as well as on the interval. The workspace never does,
//    which costs it up to 5s of tail. Here the component unmounts the moment the
//    participant advances, and the last events before that (TASK_COMPLETED /
//    TASK_TIMEOUT) are exactly the ones that must not be dropped.
export const StudyTelemetryContext = createContext()

export const StudyTelemetryProvider = ({ participantId, stepId, children }) => {
  // 1. Temporary Buffer (Flushed to the server every 5s)
  const logsBuffer = useRef([])

  // 2. Permanent Store (Keeps everything for the lifetime of the step)
  const allEvents = useRef([])

  const logTelemetryEvent = useCallback((type, payload) => {
    const event = {
      type,
      payload,
      timestamp: Date.now(),
      stepId: stepId || null
    }

    logsBuffer.current.push(event)
    allEvents.current.push(event)
  }, [stepId])

  // Log session start on mount
  useEffect(() => {
    logTelemetryEvent(SESSION_EVENTS.START, {
      window_width: window.innerWidth,
      window_height: window.innerHeight,
      timestamp: Date.now()
    })
  }, [logTelemetryEvent])

  const getAllEvents = useCallback(() => {
    return allEvents.current
  }, [])

  const flush = useCallback(() => {
    if (logsBuffer.current.length === 0) return
    if (!participantId) return

    // Swap the buffer
    const batch = logsBuffer.current
    logsBuffer.current = []

    postTelemetryBatch(participantId, batch)
      .catch(err => console.warn('Telemetry flush failed', err))
  }, [participantId])

  // Flush logs periodically, and once more on unmount (step advance).
  const flushRef = useRef(flush)
  useEffect(() => { flushRef.current = flush }, [flush])

  useEffect(() => {
    const interval = setInterval(() => flushRef.current(), 5000)
    return () => {
      clearInterval(interval)
      flushRef.current()
    }
  }, [])

  return (
    <StudyTelemetryContext.Provider value={{ logTelemetryEvent, getAllEvents }}>
      {children}
    </StudyTelemetryContext.Provider>
  )
}
